
import { supabase } from "@/integrations/supabase/client";

interface CreateFromTemplateOptions {
  templateId: string;
  userId: string;
  topic: string;
  company: string;
  primaryColor: string;
  secondaryColor: string;
  previewImageUrl: string | null;
}

export const TemplateService = {
  async createFromTemplate(options: CreateFromTemplateOptions) {
    try {
      console.log('Starting creation from template:', options.templateId);
      
      // First verify template exists
      const { data: template, error: templateError } = await supabase
        .from('course_templates')
        .select('*')
        .eq('id', options.templateId)
        .maybeSingle();

      if (templateError || !template) {
        console.error('Error finding template:', templateError);
        throw new Error('Template not found');
      }
      console.log('Found template:', template);

      // Create new resource from template
      const { data: resource, error: resourceError } = await supabase
        .from('resources')
        .insert({
          user_id: options.userId,
          topic: options.topic,
          company: options.company,
          primary_color: options.primaryColor,
          secondary_color: options.secondaryColor,
          template_id: options.templateId,
          preview_image_url: options.previewImageUrl,
          status: 'completed'
        })
        .select()
        .single();

      if (resourceError) {
        console.error('Error creating resource:', resourceError);
        throw resourceError;
      }
      console.log('Successfully created resource:', resource);

      // Get template files and copy them
      const { data: templateFiles, error: filesError } = await supabase
        .from('template_files')
        .select('*')
        .eq('template_id', options.templateId)
        .order('order_index');

      if (filesError) {
        console.error('Error fetching template files:', filesError);
        throw filesError;
      }

      const filesToInsert = [];
      if (templateFiles && templateFiles.length > 0) {
        for (const file of templateFiles) {
          filesToInsert.push({
            resource_id: resource.id,
            title: file.title,
            description: file.description,
            file_url: file.file_url,
            order_index: file.order_index,
            file_type: file.file_type || 'pdf' // Use file_type if available, otherwise default to 'pdf'
          });
        }
      }

      // The database trigger will handle copying:
      // 1. Quiz files and questions from template_quizzes and template_quiz_questions
      // 2. Drag and drop games from template_drag_drop_games
      // 3. Linking games from template_linking_games
      // 4. Free response questions from template_free_response_questions
      
      // Insert all regular files
      if (filesToInsert.length > 0) {
        const { error: insertFilesError } = await supabase
          .from('resource_files')
          .insert(filesToInsert);

        if (insertFilesError) {
          console.error('Error inserting files:', insertFilesError);
          throw insertFilesError;
        }
        console.log('Successfully created files:', filesToInsert.length);
      }

      return resource;
    } catch (error) {
      console.error('Error in createFromTemplate:', error);
      throw error;
    }
  },
  
  /**
   * Make a template from an existing resource
   * This will copy all the resource's files and activities
   */
  async createTemplateFromResource(resourceId: string, templateData: {
    title: string;
    description?: string;
    topic: string;
    company: string;
    primaryColor: string;
    secondaryColor: string;
    previewImageUrl?: string;
  }) {
    try {
      console.log('Creating template from resource:', resourceId);
      
      // First verify resource exists
      const { data: resource, error: resourceError } = await supabase
        .from('resources')
        .select('*')
        .eq('id', resourceId)
        .maybeSingle();

      if (resourceError || !resource) {
        console.error('Error finding resource:', resourceError);
        throw new Error('Resource not found');
      }
      
      // Create new template
      const { data: template, error: templateError } = await supabase
        .from('course_templates')
        .insert({
          title: templateData.title,
          description: templateData.description || '',
          topic: templateData.topic,
          company: templateData.company,
          primary_color: templateData.primaryColor,
          secondary_color: templateData.secondaryColor,
          preview_image_url: templateData.previewImageUrl
        })
        .select()
        .single();

      if (templateError) {
        console.error('Error creating template:', templateError);
        throw templateError;
      }
      
      // Get resource files and copy them to template files
      const { data: resourceFiles, error: filesError } = await supabase
        .from('resource_files')
        .select('*')
        .eq('resource_id', resourceId)
        .order('order_index');

      if (filesError) {
        console.error('Error fetching resource files:', filesError);
        throw filesError;
      }

      // Process regular files first (PDFs, videos, etc.)
      const regularFiles = resourceFiles?.filter(file => 
        !['quiz', 'drag_drop', 'linking', 'free_response'].includes(file.file_type || '')
      ) || [];
      
      if (regularFiles.length > 0) {
        const templateFiles = regularFiles.map(file => ({
          template_id: template.id,
          title: file.title,
          description: file.description,
          file_url: file.file_url,
          file_type: file.file_type,
          order_index: file.order_index
        }));
        
        const { error: insertFilesError } = await supabase
          .from('template_files')
          .insert(templateFiles);

        if (insertFilesError) {
          console.error('Error inserting template files:', insertFilesError);
          throw insertFilesError;
        }
      }
      
      // Process quizzes
      await this.copyQuizzesToTemplate(resourceId, template.id);
      
      // Process drag and drop games
      await this.copyDragDropToTemplate(resourceFiles, template.id);
      
      // Process linking games
      await this.copyLinkingToTemplate(resourceFiles, template.id);
      
      // Process free response questions
      await this.copyFreeResponseToTemplate(resourceFiles, template.id);
      
      console.log('Successfully created template from resource');
      
      return template;
    } catch (error) {
      console.error('Error in createTemplateFromResource:', error);
      throw error;
    }
  },
  
  /**
   * Copy quizzes from a resource to a template
   */
  async copyQuizzesToTemplate(resourceId: string, templateId: string) {
    try {
      // Get quizzes for the resource
      const { data: quizzes, error: quizzesError } = await supabase
        .from('quizzes')
        .select('*')
        .eq('resource_id', resourceId);
        
      if (quizzesError) {
        console.error('Error fetching quizzes:', quizzesError);
        return;
      }
      
      if (!quizzes || quizzes.length === 0) {
        console.log('No quizzes found for resource');
        return;
      }
      
      for (const quiz of quizzes) {
        // Create template quiz
        const { data: templateQuiz, error: templateQuizError } = await supabase
          .from('template_quizzes')
          .insert({
            template_id: templateId,
            title: quiz.title,
            description: quiz.description,
            passing_score: quiz.passing_score
          })
          .select()
          .single();
          
        if (templateQuizError) {
          console.error('Error creating template quiz:', templateQuizError);
          continue;
        }
        
        // Get questions for the quiz
        const { data: questions, error: questionsError } = await supabase
          .from('quiz_questions')
          .select('*')
          .eq('quiz_id', quiz.id)
          .order('order_index');
          
        if (questionsError) {
          console.error('Error fetching quiz questions:', questionsError);
          continue;
        }
        
        if (questions && questions.length > 0) {
          const templateQuestions = questions.map(q => ({
            quiz_id: templateQuiz.id,
            question: q.question,
            type: q.type,
            correct_answer: q.correct_answer,
            options: q.options,
            order_index: q.order_index
          }));
          
          const { error: insertQuestionsError } = await supabase
            .from('template_quiz_questions')
            .insert(templateQuestions);
            
          if (insertQuestionsError) {
            console.error('Error inserting template quiz questions:', insertQuestionsError);
          }
        }
      }
    } catch (error) {
      console.error('Error in copyQuizzesToTemplate:', error);
    }
  },
  
  /**
   * Copy drag and drop games from a resource to a template
   */
  async copyDragDropToTemplate(resourceFiles: any[], templateId: string) {
    try {
      // Filter drag and drop files
      const dragDropFiles = resourceFiles.filter(file => file.file_type === 'drag_drop');
      
      if (dragDropFiles.length === 0) {
        console.log('No drag and drop games found');
        return;
      }
      
      for (const file of dragDropFiles) {
        // Get the drag and drop game
        const { data: game, error: gameError } = await supabase
          .from('drag_drop_games')
          .select('*')
          .eq('file_id', file.id)
          .single();
          
        if (gameError || !game) {
          console.error('Error fetching drag and drop game:', gameError);
          continue;
        }
        
        // Create template game
        const { data: templateGame, error: templateGameError } = await supabase
          .from('template_drag_drop_games')
          .insert({
            template_id: templateId,
            title: file.title,
            description: file.description,
            passing_score: game.passing_score
          })
          .select()
          .single();
          
        if (templateGameError) {
          console.error('Error creating template drag and drop game:', templateGameError);
          continue;
        }
        
        // Copy categories
        const { data: categories, error: categoriesError } = await supabase
          .from('drag_drop_categories')
          .select('*')
          .eq('game_id', game.id);
          
        if (categoriesError) {
          console.error('Error fetching drag and drop categories:', categoriesError);
          continue;
        }
        
        if (categories && categories.length > 0) {
          const templateCategories = categories.map(c => ({
            game_id: templateGame.id,
            name: c.name
          }));
          
          const { error: insertCategoriesError } = await supabase
            .from('template_drag_drop_categories')
            .insert(templateCategories);
            
          if (insertCategoriesError) {
            console.error('Error inserting template drag and drop categories:', insertCategoriesError);
          }
        }
        
        // Copy options
        const { data: options, error: optionsError } = await supabase
          .from('drag_drop_options')
          .select('*')
          .eq('game_id', game.id);
          
        if (optionsError) {
          console.error('Error fetching drag and drop options:', optionsError);
          continue;
        }
        
        if (options && options.length > 0) {
          const templateOptions = options.map(o => ({
            game_id: templateGame.id,
            text: o.text,
            correct_category: o.correct_category
          }));
          
          const { error: insertOptionsError } = await supabase
            .from('template_drag_drop_options')
            .insert(templateOptions);
            
          if (insertOptionsError) {
            console.error('Error inserting template drag and drop options:', insertOptionsError);
          }
        }
      }
    } catch (error) {
      console.error('Error in copyDragDropToTemplate:', error);
    }
  },
  
  /**
   * Copy linking games from a resource to a template
   */
  async copyLinkingToTemplate(resourceFiles: any[], templateId: string) {
    try {
      // Filter linking files
      const linkingFiles = resourceFiles.filter(file => file.file_type === 'linking');
      
      if (linkingFiles.length === 0) {
        console.log('No linking games found');
        return;
      }
      
      for (const file of linkingFiles) {
        // Get the linking game
        const { data: game, error: gameError } = await supabase
          .from('linking_games')
          .select('*')
          .eq('file_id', file.id)
          .single();
          
        if (gameError || !game) {
          console.error('Error fetching linking game:', gameError);
          continue;
        }
        
        // Create template game
        const { data: templateGame, error: templateGameError } = await supabase
          .from('template_linking_games')
          .insert({
            template_id: templateId,
            title: file.title,
            description: file.description,
            passing_score: game.passing_score
          })
          .select()
          .single();
          
        if (templateGameError) {
          console.error('Error creating template linking game:', templateGameError);
          continue;
        }
        
        // Copy items
        const { data: items, error: itemsError } = await supabase
          .from('linking_items')
          .select('*')
          .eq('game_id', game.id);
          
        if (itemsError) {
          console.error('Error fetching linking items:', itemsError);
          continue;
        }
        
        if (items && items.length > 0) {
          const templateItems = items.map(item => ({
            game_id: templateGame.id,
            left_text: item.left_text,
            right_text: item.right_text
          }));
          
          const { error: insertItemsError } = await supabase
            .from('template_linking_items')
            .insert(templateItems);
            
          if (insertItemsError) {
            console.error('Error inserting template linking items:', insertItemsError);
          }
        }
      }
    } catch (error) {
      console.error('Error in copyLinkingToTemplate:', error);
    }
  },
  
  /**
   * Copy free response questions from a resource to a template
   */
  async copyFreeResponseToTemplate(resourceFiles: any[], templateId: string) {
    try {
      // Filter free response files
      const freeResponseFiles = resourceFiles.filter(file => file.file_type === 'free_response');
      
      if (freeResponseFiles.length === 0) {
        console.log('No free response questions found');
        return;
      }
      
      for (const file of freeResponseFiles) {
        // Get free response questions
        const { data: questions, error: questionsError } = await supabase
          .from('free_response_questions')
          .select('*')
          .eq('file_id', file.id)
          .order('order_index');
          
        if (questionsError) {
          console.error('Error fetching free response questions:', questionsError);
          continue;
        }
        
        if (questions && questions.length > 0) {
          const templateQuestions = questions.map(q => ({
            template_id: templateId,
            question: q.question,
            order_index: q.order_index
          }));
          
          const { error: insertQuestionsError } = await supabase
            .from('template_free_response_questions')
            .insert(templateQuestions);
            
          if (insertQuestionsError) {
            console.error('Error inserting template free response questions:', insertQuestionsError);
          }
        }
      }
    } catch (error) {
      console.error('Error in copyFreeResponseToTemplate:', error);
    }
  }
};
